import { useMutation } from '@apollo/client';
import { useCallback, useRef } from 'react';

import { CreatePaymentMutation } from '@/__generated_graphql__/graphql';
import {
  AdTypeEnum,
  MediumEnum,
  PaidService,
  PaymentCreateInput,
  PaymentCreateStatusEnum,
  PaymentMethodEnum,
  PaymentResult
} from '@/__generated_graphql__/types';
import { EMPTY_BALANCE_CODE } from '@/components/AdvertisePayments/AdvertisePayments.constants';
import {
  createFailedResult,
  isWalletMethod,
  mapTypenameToAdType,
  selectOptionPrice,
  selectWalletBalance
} from '@/components/AdvertisePayments/AdvertisePayments.helpers';
import { HandlePaymentArgs } from '@/components/AdvertisePayments/hooks/types';
import { useModal } from '@/components/AdvertisePayments/hooks/useModal';
import { usePaymentMessageListener } from '@/components/AdvertisePayments/hooks/usePaymentMessageListener';
import { usePaymentModalsManager } from '@/components/AdvertisePayments/hooks/usePaymentModalsManager';
import { PaymentTypeEnum } from '@/constants/common';
import { CREATE_PAYMENT } from '@/graphql/mutations/payments.mutations';
import { CardAd } from '@/types';

import { ExtraContentModalProps } from '../components/ExtraContent/ExtraContent.types';

export type UsePaymentHandlerArgs = {
  modals: ReturnType<typeof usePaymentModalsManager>;
  mainModal: ReturnType<typeof useModal>;
  adId?: string | null;
  ad?: CardAd;
  platform?: MediumEnum;
  onError?: () => void;
  onSuccess?: (method?: PaymentMethodEnum) => void | Promise<void>;
  type?: PaymentTypeEnum;
  paidServices: PaidService[];
};

export const usePaymentHandler = ({
  ad,
  platform = MediumEnum.Desktop,
  mainModal,
  modals,
  onSuccess,
  onError,
  type = PaymentTypeEnum.ADVERTISE,
  paidServices
}: UsePaymentHandlerArgs) => {
  const [createPayment, { loading, error: gqlError }] = useMutation<
    CreatePaymentMutation,
    PaymentCreateInput
  >(CREATE_PAYMENT);

  const paymentCompletedRef = useRef(false);
  const popupWatchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const targetId = ad?.id;
  // eslint-disable-next-line no-underscore-dangle
  const typeName = ad?.__typename;
  const adTypeForMutation: AdTypeEnum | null = typeName
    ? mapTypenameToAdType(typeName)
    : null;

  const clearPopupInterval = () => {
    if (popupWatchIntervalRef.current) {
      clearInterval(popupWatchIntervalRef.current);
      popupWatchIntervalRef.current = null;
    }
  };

  const markPaymentAsFinished = () => {
    paymentCompletedRef.current = true;
  };

  usePaymentMessageListener(
    () => {
      markPaymentAsFinished();
      clearPopupInterval();
      onSuccess?.();
    },
    () => {
      clearPopupInterval();
      mainModal.close();
      modals.error.open();
      onError?.();
    }
  );

  const executePayment = async ({
    paidServiceId,
    targetId: execTargetId,
    platform: execPlatform,
    paymentMethodId,
    amount,
    isAddBalance
  }: HandlePaymentArgs): Promise<PaymentResult> => {
    if (!paidServiceId) {
      return createFailedResult(
        'INVALID_INPUT',
        'Missing required payment input fields.'
      );
    }

    let computedVariables: PaymentCreateInput = {
      paidServiceId,
      paymentMethodId,
      platform: execPlatform,
      amount: amount ?? null
    };

    if (!isAddBalance) {
      computedVariables = {
        ...computedVariables,
        targetId: execTargetId,
        targetType: adTypeForMutation
      };
    }

    try {
      const { data } = await createPayment({
        variables: computedVariables
      });
      const result = data?.createPayment?.paymentResult;

      if (!result) {
        return createFailedResult(
          'NO_RESULT',
          'No payment result returned from server.'
        );
      }

      return { ...result, errors: result.errors ?? [] };
    } catch (err) {
      console.error('Payment error:', err);
      return createFailedResult(
        'NETWORK_ERROR',
        err instanceof Error ? err.message : String(err)
      );
    }
  };

  const handleConfirmPayment = useCallback(
    async (
      method: PaymentMethodEnum,
      paidServiceOptionId: ExtraContentModalProps['activePaidServiceId'],
      isAddBalance?: boolean
    ): Promise<PaymentResult | null> => {
      if (!paidServiceOptionId || (isAddBalance ? false : !targetId))
        return null;

      let optionPrice: number | null;
      if (typeof paidServiceOptionId === 'string')
        optionPrice = selectOptionPrice(paidServices, paidServiceOptionId);
      else {
        optionPrice = paidServiceOptionId.price;
      }

      if (isWalletMethod(method)) {
        const walletBalance = selectWalletBalance(paidServices);
        if (typeof optionPrice === 'number' && walletBalance < optionPrice) {
          modals.emptyBalance.open();
          return null;
        }
      }

      // Open a blank window immediately to avoid popup blockers
      const paymentWindow = window.open('', '_blank');

      try {
        const computedPaidServiceId: string =
          !!paidServiceOptionId && typeof paidServiceOptionId !== 'string'
            ? paidServiceOptionId.serviceId
            : paidServiceOptionId;

        const result = await executePayment({
          paidServiceId: computedPaidServiceId,
          targetId,
          paymentMethodId: method,
          platform,
          isAddBalance,
          amount: optionPrice
        });

        switch (result.status) {
          case PaymentCreateStatusEnum.Success: {
            mainModal.close();
            onSuccess?.(method);
            paymentWindow?.close();
            break;
          }

          case PaymentCreateStatusEnum.Redirect: {
            if (result.redirectUrl && paymentWindow) {
              paymentWindow.location.href = result.redirectUrl;
              clearPopupInterval();
              popupWatchIntervalRef.current = setInterval(() => {
                if (!paymentWindow || paymentWindow.closed) {
                  clearPopupInterval();
                  if (!paymentCompletedRef.current) {
                    console.warn(
                      '[Payment] Window closed but payment not confirmed.'
                    );
                    mainModal.close();
                    const computedModal = isAddBalance
                      ? modals.errorTopUp
                      : modals.error;
                    computedModal.open();
                    onError?.();
                  }
                }
              }, 500);
            }
            break;
          }

          case PaymentCreateStatusEnum.Failure:
          default: {
            const emptyBalance =
              Array.isArray(result.errors) &&
              result.errors.some(
                error =>
                  (typeof error.code === 'string' &&
                    (EMPTY_BALANCE_CODE as readonly string[]).includes(
                      error.code
                    )) ||
                  (typeof error.message === 'string' &&
                    /no | balance/i.test(error.message))
              );

            mainModal.close();
            if (emptyBalance) {
              modals.emptyBalance.open();
            } else {
              const computedModal = isAddBalance
                ? modals.errorTopUp
                : modals.error;
              computedModal.open();
            }
            onError?.();
            break;
          }
        }

        return result;
      } catch {
        mainModal.close();
        return null;
      }
    },
    [
      targetId,
      adTypeForMutation,
      platform,
      executePayment,
      mainModal,
      modals,
      onError,
      onSuccess,
      paidServices
    ]
  );

  const handleRetryPayment = () => {
    if (type === PaymentTypeEnum.PUBLICATION) {
      modals.error.close();
      return;
    }
    mainModal.open();
    modals.error.close();
  };

  return {
    loading,
    gqlError,
    handleConfirmPayment,
    handleRetryPayment,
    markPaymentAsFinished,
    clearPopupInterval
  };
};
